import {
  HubConnectionBuilder,
  HubConnection,
  HubConnectionState,
  IRetryPolicy,
  RetryContext,
} from "@microsoft/signalr";
import { getFreshAccessToken } from "../auth/authClient";

/**
 * Indefinite reconnect with a capped backoff. The default withAutomaticReconnect() gives up after ~30s (4 tries),
 * so a backend respawn that takes longer leaves the hub permanently dead even after the API recovers. This keeps
 * trying until the server is back.
 */
class InfiniteRetryPolicy implements IRetryPolicy {
  private readonly schedule = [0, 2000, 5000, 10000];

  nextRetryDelayInMilliseconds(context: RetryContext): number | null {
    return context.previousRetryCount < this.schedule.length
      ? this.schedule[context.previousRetryCount]
      : 30000; // keep retrying every 30s indefinitely
  }
}

export type SignalConnectionState = "connected" | "connecting" | "disconnected";

export class Signal {
  public get connected(): boolean {
    return this.connection.state === HubConnectionState.Connected;
  }

  // A simplified, render-friendly view of the live hub state (green / yellow / red).
  public get connectionState(): SignalConnectionState {
    switch (this.connection.state) {
      case HubConnectionState.Connected:
        return "connected";
      case HubConnectionState.Connecting:
      case HubConnectionState.Reconnecting:
        return "connecting";
      default:
        return "disconnected";
    }
  }

  private connection: HubConnection;
  private active = false; // the user is authenticated and the hub should be up
  private starting = false;
  private startTimer?: ReturnType<typeof setTimeout>;
  // External-store listeners for connection-state changes (used via useSyncExternalStore), so a
  // liveness indicator can react WITHOUT routing connection state through React context (which would
  // re-render every consumer on each transition).
  private readonly stateListeners = new Set<() => void>();

  /** Subscribe to connection-state transitions; returns an unsubscribe fn. */
  public subscribeConnection(listener: () => void): () => void {
    this.stateListeners.add(listener);
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  private notifyStateChange(): void {
    this._fn?.();
    this.stateListeners.forEach((listener) => listener());
  }

  constructor(
    url: string,
    private _fn?: () => void,
  ) {
    this.connection = new HubConnectionBuilder()
      .withUrl(url, {
        // Auth-aware: refresh the token (single-flight) before each (re)connect if it's expired, so a genuine
        // expiry recovers instead of looping on a stale token. Reads the LIVE token otherwise.
        accessTokenFactory: () => getFreshAccessToken(),
      })
      .withAutomaticReconnect(new InfiniteRetryPolicy())
      .build();

    this.connection.onreconnected(() => this.notifyStateChange());
    this.connection.onreconnecting(() => this.notifyStateChange());
    this.connection.onclose(() => {
      this.notifyStateChange();
      // If we still want to be connected (not an explicit logout/close), keep trying to come back.
      if (this.active) this.scheduleStart(2000);
    });
  }

  public start(token?: string): void {
    this.active = true;
    this.connectNow();
  }

  public close(): void {
    this.active = false;
    if (this.startTimer) clearTimeout(this.startTimer);
    try {
      this.connection.stop().catch(() => {});
    } catch {}
  }

  private connectNow(): void {
    if (this.starting) return;
    if (this.connection.state !== HubConnectionState.Disconnected) return;

    this.starting = true;
    this.notifyStateChange(); // surface the "connecting" transition to the UI
    this.connection
      .start()
      .then(() => {
        this.starting = false;
        this.notifyStateChange();
      })
      .catch(() => {
        // Initial connect failed (e.g. backend still coming up) — retry; withAutomaticReconnect only kicks in
        // AFTER a first successful connection, so the initial attempt needs its own retry.
        this.starting = false;
        this.notifyStateChange();
        if (this.active) this.scheduleStart(3000);
      });
  }

  private scheduleStart(delayMs: number): void {
    if (!this.active) return;
    if (this.startTimer) clearTimeout(this.startTimer);
    this.startTimer = setTimeout(() => this.connectNow(), delayMs);
  }

  public on(name: string, fn: (...args: any[]) => void) {
    this.connection.on(name, fn);
  }

  public off(name: string, fn: (...args: any[]) => void) {
    this.connection.off(name, fn);
  }

  public send(name: string, ...args: any[]) {
    this.connection.send(name, ...args);
  }
}
